import React from "react";
import { Button, MenuItem, TextField, Tooltip } from "@material-ui/core";

import { hasDriverStops } from "../../../utils/driverStops";
import { sessionYardDriverStops } from "../../../utils/gateAccess";
import { GATE_ALL_STATUSES } from "./gateCheckInUtils";

/** Status and dock inputs shared by the Gate Queue table and the shipment
 * dispatch tabs. The "form" variant wears outlined labelled inputs so the
 * control sits level with the dispatch tab's date pickers; the "table"
 * variant stays bare and narrow for a dense queue row. The save button is
 * queue-only — on the dispatch tabs the form's primary Save persists the
 * pending gate edit. */

const isTable = (variant) => variant === "table";

/** `isStatusAllowed` greys out the statuses this row may not move to — a
 * supervisor calling drivers out of the queue order, or anyone departing a
 * visit the truck never docked for; `restriction` is the reason, shown on the
 * whole control — a disabled menu item carries no tooltip of its own, and the
 * menu is shut when the reason matters. Only the queue order has one: DEPARTED
 * greyed on an undocked row is left to speak for itself. Both are optional:
 * with neither, every status is offered. */
export const GateStatusSelect = ({
  editor,
  variant = "form",
  isStatusAllowed,
  restriction,
}) => {
  const select = (
    <TextField
      select
      label={isTable(variant) ? undefined : "Gate Status"}
      variant={isTable(variant) ? "standard" : "outlined"}
      margin="dense"
      style={{ minWidth: isTable(variant) ? 120 : 160 }}
      value={editor.status}
      onChange={(event) => editor.setStatus(event.target.value)}
      disabled={editor.saving || editor.conflict}
    >
      {GATE_ALL_STATUSES.map((status) => (
        <MenuItem
          key={status}
          value={status}
          disabled={Boolean(isStatusAllowed) && !isStatusAllowed(status)}
        >
          {status.replace("_", " ")}
        </MenuItem>
      ))}
    </TextField>
  );
  return restriction ? (
    <Tooltip title={restriction}>
      <span>{select}</span>
    </Tooltip>
  ) : (
    select
  );
};

/** Who is driving. Editable on a row the office wrote for a truck that never
 * reached the kiosk — the manager asks the driver and fills it in. Locked on a
 * row a signed-in driver checked in against: that name came from their
 * account, and letting the office retype it would quietly replace verified
 * identity with a guess. `readOnly` is the caller's call because only it knows
 * whether the row carries a carrierUserId. */
export const GateDriverField = ({ editor, readOnly, variant = "form" }) => (
  <TextField
    label={isTable(variant) ? undefined : "Driver"}
    placeholder={readOnly ? undefined : "Driver name"}
    variant={isTable(variant) ? "standard" : "outlined"}
    margin="dense"
    style={{ width: isTable(variant) ? 130 : 170 }}
    value={editor.driverName}
    onChange={(event) => editor.setDriverName(event.target.value)}
    InputProps={{ readOnly: Boolean(readOnly) }}
    disabled={editor.saving || editor.conflict}
  />
);

export const GatePlateField = ({ editor, variant = "form" }) => (
  <TextField
    label={isTable(variant) ? undefined : "Plate"}
    placeholder="Plate #"
    variant={isTable(variant) ? "standard" : "outlined"}
    margin="dense"
    style={{ width: isTable(variant) ? 90 : 110 }}
    value={editor.truckPlate}
    onChange={(event) => editor.setTruckPlate(event.target.value)}
    disabled={editor.saving || editor.conflict}
  />
);

/** Where the truck goes next: a dropdown of the yard's stops (Driver check-in
 * tab). The stored value is the stop's label, and driverApi turns it into
 * that stop's message for the driver. A yard with no stops has nowhere to
 * send a truck, so the field is not rendered there at all — unless the row
 * already carries a value, which is then shown so it can be read or cleared.
 *
 * A row whose value is not among the stops (typed before the list existed, or
 * a stop since renamed) keeps that value as an extra menu entry rather than
 * reading as blank: blank would look like "unassigned" and a save would then
 * quietly clear what the gate had written. */
export const GateDockField = ({ editor, variant = "form" }) => {
  const stops = sessionYardDriverStops();
  const labels = stops.map((stop) => stop.label);
  const orphan = editor.dock && !labels.includes(editor.dock) ? editor.dock : null;
  if (!hasDriverStops(stops) && !orphan) return null;
  return (
    <TextField
      select
      label={isTable(variant) ? undefined : "Stop"}
      variant={isTable(variant) ? "standard" : "outlined"}
      margin="dense"
      value={editor.dock}
      onChange={(event) => editor.setDock(event.target.value)}
      disabled={editor.saving || editor.conflict}
      style={{ minWidth: isTable(variant) ? 120 : 160 }}
      // The bare table row has no label to hold the field's name, so the
      // empty choice carries it — otherwise an unassigned row is a blank box.
      SelectProps={{
        displayEmpty: true,
        renderValue: (value) => value || (isTable(variant) ? "— stop —" : ""),
      }}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {orphan ? (
        <MenuItem value={orphan}>{orphan} (not a listed stop)</MenuItem>
      ) : null}
      {labels.map((label) => (
        <MenuItem key={label} value={label}>
          {label}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const GateSaveButton = ({ editor }) =>
  editor.conflict ? (
    <Button color="secondary" size="small" onClick={editor.clearConflict}>
      Reload
    </Button>
  ) : (
    <Button
      color="primary"
      size="small"
      onClick={editor.save}
      disabled={editor.saving || !editor.dirty}
    >
      Save
    </Button>
  );
